import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TranslationContext } from 'contexts/translation-context';
import styled from 'styled-components';
import { openModal } from 'components/common/modal/modal-actions';
import { FormikProps } from 'formik';
import { JoinFormType } from 'types/join';
import { DEFAULT_ACH_COUNTRIES } from 'constants/plan';
import { useExecuteRecaptcha } from 'hooks/useExecuteRecaptcha';
import { PaymentErrorModal } from './payment-error-modal';
import { fetchPaymentStatus } from '../actions';
import { PlanState } from '../reducers/plan';

const PaymentView = styled.div`
  width: 100%;
`;

const PaymentHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const PaymentTitle = styled.h1`
  font-family: ${props => props.theme.fonts.FunnelDisplay};
  font-size: 36px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 1.12;
  color: ${props => props.theme.colors.black};
  margin: 0 0 8px;

  @media (max-width: 999px) {
    font-size: 24px;
  }
`;

const AmountDue = styled.div`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 16px;
  color: ${props => props.theme.colors.siteBlack};

  @media (max-width: 999px) {
    font-size: 14px;
  }
`;

const PaymentCard = styled.div`
  border: 1px solid ${props => props.theme.colors.siteCardBorder};
  border-radius: 16px;
  background-color: ${props => props.theme.colors.siteCardBackground};
  padding: 28px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  gap: 18px;

  @media (max-width: 999px) {
    padding: 18px;
  }
`;

const IframeShell = styled.div`
  width: 100%;
  min-height: 640px;
  border: 1px solid ${props => props.theme.colors.siteCardBorder};
  border-radius: 12px;
  background-color: ${props => props.theme.colors.white};
  overflow: hidden;
  box-sizing: border-box;
  position: relative;

  @media (max-width: 999px) {
    min-height: 560px;
  }
`;

const PaymentIframe = styled.iframe`
  display: block;
  width: 100%;
  height: 640px;
  border: 0;
  background-color: ${props => props.theme.colors.white};

  @media (max-width: 999px) {
    height: 560px;
  }
`;

const FallbackBox = styled.div<{ elevated?: boolean }>`
  border: 1px solid
    ${props =>
      props.elevated ? props.theme.colors.siteIndigoBorder : props.theme.colors.siteCardBorder};
  border-radius: 12px;
  background-color: ${props =>
    props.elevated ? props.theme.colors.siteIndigoLighter : props.theme.colors.white};
  padding: 16px 18px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;

  @media (max-width: 999px) {
    align-items: flex-start;
    flex-direction: column;
  }
`;

const FallbackText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const FallbackTitle = styled.div`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 16px;
  color: ${props => props.theme.colors.black};
`;

const FallbackHint = styled.div`
  font-family: ${props => props.theme.fonts.interSans};
  font-size: 13px;
  color: ${props => props.theme.colors.contactsGrey};
  line-height: 1.45;
`;

const OpenPaymentLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  padding: 0 18px;
  border-radius: 10px;
  background: ${props => props.theme.colors.siteIndigo};
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 14px;
  text-decoration: none;
  white-space: nowrap;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover,
  &:focus {
    background: ${props => props.theme.colors.siteIndigoHover};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.siteIndigoLight};
    outline: none;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 999px) {
    width: 100%;
    box-sizing: border-box;
  }
`;

const TermsBox = styled.div`
  border-top: 1px solid ${props => props.theme.colors.siteCardBorder};
  padding-top: 18px;
  font-family: ${props => props.theme.fonts.interSans};
  color: ${props => props.theme.colors.siteBlack};
  font-size: 13px;
  line-height: 1.55;
`;

const TermsTitle = styled.div`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  margin-bottom: 8px;
`;

const TermsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const TermLabel = styled.span`
  font-family: ${props => props.theme.fonts.interSansSemiBold};
`;

const TermText = styled.span`
  color: ${props => props.theme.colors.contactsGrey};
`;

const PreviousButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 24px;
  padding: 0 24px;
  min-width: 144px;
  height: 44px;
  border: 0;
  border-radius: 10px;
  background: ${props => props.theme.colors.siteIndigo};
  color: ${props => props.theme.colors.white};
  font-family: ${props => props.theme.fonts.interSansSemiBold};
  font-size: 16px;
  cursor: pointer;
  transition:
    background-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover,
  &:focus {
    background: ${props => props.theme.colors.siteIndigoHover};
    box-shadow: 0 0 0 3px ${props => props.theme.colors.siteIndigoLight};
    outline: none;
  }

  &:active {
    transform: translateY(1px);
  }

  @media (max-width: 999px) {
    width: 100%;
    font-size: 14px;
  }
`;

type PaymentDetailsProps = {
  id: string;
  setShowSuccessPage: (data: boolean) => void;
  setShowSuccessPageForPriceList: (data: boolean) => void;
  form: FormikProps<JoinFormType>;
  removeCacheForm: () => void;
  finalTotalPrice: number;
  onPrevious: () => void;
};
export const PaymentDetailsSection = ({
  id,
  setShowSuccessPage,
  setShowSuccessPageForPriceList,
  form,
  removeCacheForm,
  finalTotalPrice,
  onPrevious
}: PaymentDetailsProps) => {
  const TRANSLATION_TEXT = useContext(TranslationContext) as { [key: string]: string };
  const dispatch = useDispatch();
  const { opportunityId, paymentLink, paymentStatus } =
    useSelector((state: { plan: PlanState }) => state.plan) || {};
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [showIframeFallback, setShowIframeFallback] = useState(false);
  const hasHandledPaymentStatusRef = useRef(false);
  const intervalRef = useRef<number | null>(null);
  const counterRef = useRef(0);
  const { execute } = useExecuteRecaptcha();

  const resolvedPaymentLink = useMemo(() => {
    if (!paymentLink) {
      return null;
    }
    const companyCountry = form.values.companyCountry;
    const shouldUseAchPaymentMethod =
      CONFIG.__ENV !== 'production' &&
      companyCountry &&
      DEFAULT_ACH_COUNTRIES.some(name => name.toLowerCase() === companyCountry.toLowerCase());
    if (!shouldUseAchPaymentMethod || paymentLink.includes('c__payment_method=')) {
      return paymentLink;
    }
    const separator = paymentLink.includes('?') ? '&' : '?';
    return `${paymentLink}${separator}c__payment_method=ach`;
  }, [form.values.companyCountry, paymentLink]);

  const openErrorPaymentModal = () => {
    dispatch(
      openModal({
        component: <PaymentErrorModal />,
        modalClassName: 'registrationPaymentFaildModal'
      })
    );
  };

  const fetchStatus = useCallback(() => {
    if (opportunityId && form.values.email) {
      execute({
        callback: fetchPaymentStatus,
        params: [opportunityId, form.values.email]
      });
    }
  }, [execute, form.values.email, opportunityId]);

  const startPaymentPolling = useCallback(() => {
    fetchStatus();
    if (intervalRef.current === null && counterRef.current < 10) {
      const nextIntervalId = window.setInterval(() => {
        counterRef.current += 1;
        if (counterRef.current >= 10) {
          window.clearInterval(nextIntervalId);
          intervalRef.current = null;
          return;
        }
        fetchStatus();
      }, 15000);
      intervalRef.current = nextIntervalId;
    }
  }, [fetchStatus]);

  useEffect(() => {
    if (!opportunityId || !form.values.email) {
      return;
    }
    fetchStatus();
  }, [fetchStatus, form.values.email, opportunityId]);

  useEffect(() => {
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (paymentLink && opportunityId) {
      startPaymentPolling();
    }
  }, [opportunityId, paymentLink, startPaymentPolling]);

  useEffect(() => {
    if (!resolvedPaymentLink || iframeLoaded) {
      return undefined;
    }

    const fallbackTimer = window.setTimeout(() => {
      setShowIframeFallback(true);
    }, 4000);

    return () => {
      window.clearTimeout(fallbackTimer);
    };
  }, [iframeLoaded, resolvedPaymentLink]);

  useEffect(() => {
    if (hasHandledPaymentStatusRef.current) {
      return;
    }

    if (paymentStatus === 'Failed') {
      hasHandledPaymentStatusRef.current = true;
      openErrorPaymentModal();
    } else if (paymentStatus === 'Paid' || paymentStatus === 'AchPaymentInVerification') {
      hasHandledPaymentStatusRef.current = true;
      setShowSuccessPageForPriceList(true);
      removeCacheForm();
    } else if (paymentStatus === 'Authorize') {
      hasHandledPaymentStatusRef.current = true;
      setShowSuccessPage(true);
      removeCacheForm();
    }
  }, [paymentStatus, removeCacheForm, setShowSuccessPage, setShowSuccessPageForPriceList]);

  return (
    <PaymentView id={id}>
      <PaymentHeader>
        <PaymentTitle>{TRANSLATION_TEXT.payment_details || TRANSLATION_TEXT.payment}</PaymentTitle>
        <AmountDue>
          {TRANSLATION_TEXT.amount_due || 'Amount due'}: ${finalTotalPrice.toFixed(2)}
        </AmountDue>
      </PaymentHeader>
      <PaymentCard>
        <IframeShell>
          {resolvedPaymentLink && (
            <PaymentIframe
              title={TRANSLATION_TEXT.payment || 'Payment'}
              src={resolvedPaymentLink}
              onLoad={() => setIframeLoaded(true)}
              onError={() => setShowIframeFallback(true)}
            />
          )}
        </IframeShell>
        {resolvedPaymentLink && (
          <FallbackBox elevated={showIframeFallback}>
            <FallbackText>
              <FallbackTitle>
                {showIframeFallback
                  ? TRANSLATION_TEXT.open_payment_in_new_tab || 'Open payment in a new tab'
                  : TRANSLATION_TEXT.secured_transaction || 'Secured Transaction'}
              </FallbackTitle>
              <FallbackHint>
                {showIframeFallback
                  ? TRANSLATION_TEXT.payment_iframe_fallback ||
                    'If the payment form does not load here, your browser may be blocking it.'
                  : TRANSLATION_TEXT.payment_iframe_hint ||
                    'Complete the payment below, or open it in a new tab if needed.'}
              </FallbackHint>
            </FallbackText>
            <OpenPaymentLink href={resolvedPaymentLink} target="_blank" rel="noopener noreferrer">
              {TRANSLATION_TEXT.open_payment || 'Open Payment'}
            </OpenPaymentLink>
          </FallbackBox>
        )}
        <TermsBox>
          <TermsTitle>{TRANSLATION_TEXT.membership_payment_terms}</TermsTitle>
          <TermsRow>
            <TermLabel>{TRANSLATION_TEXT.recurring_payments}</TermLabel>
            <TermText>{TRANSLATION_TEXT.membership_fees_are_billed_automatically}</TermText>
          </TermsRow>
          <TermsRow>
            <TermLabel>{TRANSLATION_TEXT.billing_cycle}</TermLabel>
            <TermText>
              {
                TRANSLATION_TEXT.charges_are_processed_within_14_days_before_your_subscription_renewal_date
              }
            </TermText>
          </TermsRow>
        </TermsBox>
      </PaymentCard>
      <PreviousButton type="button" onClick={onPrevious}>
        ← {TRANSLATION_TEXT.previous || 'Previous'}
      </PreviousButton>
    </PaymentView>
  );
};

export default PaymentDetailsSection;
